// @flow
import React, { Component } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Modal from '../../../../../elements/Modal/Modal';
import { FormValidator } from '../../../../../utils/form/index';
import Select from '../../../../../elements/Select/Select';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../../local-values/utils/countries';
import Cleave from 'cleave.js/react';
import CleavePhone from 'cleave.js/dist/addons/cleave-phone.i18n';


import commonStyle from '../../../PreferencesViewCommon.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import formStyle from '../../../../../assets/styles/form.scss';

import type { FormValidationResult } from '../../../../../utils/form/index';
import type { Country } from '../../../../../entities/index';
import type { Option } from '../../../../../elements/Select/Select';
import type { Payment } from '../../../../../entities';
import type { LocalValuesState } from '../../../../local-values/duck';

type Props = {
  +handleClose: () => void;
  +postPayment: (payment: Payment) => void;
  +editPayment: (payment: Payment) => void;
  +payment?: Payment;
  +isPutting?: boolean;
  +isPosting?: boolean;
  +paymentModalVisible: boolean;
  +cleanModalState?: boolean;
  +localValues: LocalValuesState;
};

type State = {
  cardNumber: {
    value: string;
    errors?: string[];
  };
  cardType: string;
  country: {
    value: Country;
    errors?: string[];
  };
  CSV: {
    value: string;
    errors?: string[];
  };
  expireMonth: {
    value: string;
    errors?: string[];
  };
  expireYear: {
    value: string;
    errors?: string[];
  };
  firstName: {
    value: string;
    errors?: string[];
  };
  lastName: {
    value: string;
    errors?: string[];
  };
  zipCode: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  title: {
    add: {
      id: 'preferences.payments.newPayment.title',
    },
    edit: {
      id: 'preferences.payments.editPayment.title',
    }
  },
  cardNumber: {
    label: {
      id: 'preferences.payments.cardNumber.label',
    },
    placeholder: {
      id: 'preferences.payments.cardNumber.placeholder',
    },
  },
  country: {
    label: {
      id: 'address.country.label',
    },
    placeholder: {
      id: 'address.country.placeholder',
    },
  },
  CSV: {
    label: {
      id: 'preferences.payments.CSV.label',
    },
    placeholder: {
      id: 'preferences.payments.CSV.placeholder',
    },
  },
  expirationDate: {
    label: {
      id: 'preferences.payments.expirationDate.label',
    }
  },
  expireMonth: {
    placeholder: {
      id: 'preferences.payments.expireMonth.placeholder',
    },
  },
  expireYear: {
    placeholder: {
      id: 'preferences.payments.expireYear.placeholder',
    },
  },
  firstName: {
    label: {
      id: 'address.firstName.label',
    },
    placeholder: {
      id: 'address.firstName.placeholder',
    },
  },
  lastName: {
    label: {
      id: 'address.lastName.label',
    },
    placeholder: {
      id: 'address.lastName.placeholder',
    },
  },
  zipCode: {
    label: {
      id: 'address.zipCode.label',
    },
    placeholder: {
      id: 'address.zipCode.placeholder',
    },
  },
  button: {
    cancel: {
      id: 'preferences.payments.button.cancel',
    },
    add: {
      id: 'preferences.payments.button.add',
    },
    edit: {
      id: 'preferences.payments.button.edit',
    },
  }
});

const initialState = {
  cardNumber: { value: '', },
  cardType: '',
  country: { value: '', errors: undefined, },
  CSV: { value: '', },
  expireMonth: { value: '', },
  expireYear: { value: '', },
  firstName: { value: '' },
  lastName: { value: '', },
  zipCode: { value: '', },
};

export default class PaymentModal extends Component<Props, State> {
  formValidator: FormValidator;
  state = initialState;

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      cardNumber: { required: 'required', },
      country: { required: 'required', },
      CSV: { required: 'required', },
      expireMonth: { required: 'required', },
      expireYear: { required: 'required', },
      firstName: { required: 'required', },
      lastName: { required: 'required', },
      zipCode: { required: 'required', },
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    const { payment, isPosting, isPutting, cleanModalState } = nextProps;
    if (cleanModalState) {
      return this.setState(initialState);
    }
    if (payment && !(isPosting || isPutting)) {
      this.setState(
        {
          cardNumber: { value: payment.cardNumber },
          country: { value: payment.country },
          CSV: { value: payment.CSV },
          expireMonth: { value: payment.expireMonth },
          expireYear: { value: payment.expireYear },
          firstName: { value: payment.firstName },
          lastName: { value: payment.lastName },
          zipCode: { value: payment.zipCode },
        });
    }
  }

  handleChangeCountry(option: Option) {
    this.setState({
      country: {
        value: option ? option.value : undefined,
      }
    });
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleCardTypeChange(cardType?: string) {
    if (cardType) {
      this.setState({ cardType });
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { postPayment, payment, editPayment } = this.props;
    const { cardNumber, country, CSV, expireMonth, expireYear, firstName, lastName, zipCode, } = this.state;
    const errors = this.formValidator.validateForm({
      cardNumber: cardNumber.value,
      country: country.value,
      CSV: CSV.value,
      expireMonth: expireMonth.value,
      expireYear: expireYear.value,
      firstName: firstName.value,
      lastName: lastName.value,
      zipCode: zipCode.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      if (payment) {
        editPayment({
          id: payment.id,
          cardNumber: cardNumber.value,
          country: country.value,
          CSV: CSV.value,
          expireMonth: expireMonth.value,
          expireYear: expireYear.value,
          firstName: firstName.value,
          lastName: lastName.value,
          zipCode: zipCode.value,
        });
      } else {
        postPayment({
          cardNumber: cardNumber.value,
          country: country.value,
          CSV: CSV.value,
          expireMonth: expireMonth.value,
          expireYear: expireYear.value,
          firstName: firstName.value,
          lastName: lastName.value,
          zipCode: zipCode.value,
        });
      }
    }
  }

  handleCancel(e?: Event) {
    if (e) e.preventDefault();
    this.props.handleClose();
  }

  render() {
    const { paymentModalVisible, isPutting, isPosting, payment, localValues } = this.props;
    const { cardNumber, country, CSV, expireMonth, expireYear, firstName, lastName, zipCode, } = this.state;
    //console.log(admin1);
    const options: Option[] = (localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      value: country.key,
      label: getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
    }));
    const cardNumberError = cardNumber.errors && FormValidator.buildErrorLabel(cardNumber.errors, {
      required: 'preferences.payments.cardNumber.error.required',
    });
    const countryError = country.errors && FormValidator.buildErrorLabel(country.errors, {
      required: 'address.country.error.required',
    });
    const CSVError = CSV.errors && FormValidator.buildErrorLabel(CSV.errors, {
      required: 'preferences.payments.CSV.error.required',
    });
    const expireMonthError = expireMonth.errors && FormValidator.buildErrorLabel(expireMonth.errors, {
      required: 'preferences.payments.expireMonth.error.required',
    });
    const expireYearError = expireYear.errors && FormValidator.buildErrorLabel(expireYear.errors, {
      required: 'preferences.payments.expireYear.error.required',
    });
    const firstNameError = firstName.errors && FormValidator.buildErrorLabel(firstName.errors, {
      required: 'address.firstName.error.required',
    });
    const lastNameError = lastName.errors && FormValidator.buildErrorLabel(lastName.errors, {
      required: 'address.lastName.error.required',
    });
    const zipCodeError = zipCode.errors && FormValidator.buildErrorLabel(zipCode.errors, {
      required: 'address.zipCode.error.required',
    });
    return (
      <Modal open={paymentModalVisible}
             handleClose={this.handleCancel.bind(this)}
             width={770}
             height={580}>
        <form className={classNames(commonStyle.modal, formStyle.formWrap)}>
          <FormattedMessage id={payment ? messages.title.edit.id : messages.title.add.id}>{
            txt => <h2>{txt}</h2>
          }</FormattedMessage>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.cardNumber.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.cardNumber.placeholder.id}>{
                txt => <Cleave disabled={isPosting || isPutting}
                               className={classNames(inputStyle.input, { [inputStyle.red]: cardNumber.errors && cardNumber.errors.length },
                                 { [inputStyle.lightBlue]: !!cardNumber.value })}
                               value={cardNumber.value}
                               onChange={this.handleChange.bind(this)}
                               options={{
                                 creditCard: true, creditCardStrictMode: true,
                                 onCreditCardTypeChanged: this.handleCardTypeChange.bind(this)
                               }}
                               placeholder={txt}
                               name="cardNumber"/>
              }</FormattedMessage>
              {cardNumberError}
            </article>
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap)}>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.expirationDate.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <section className={classNames(formStyle.row, formStyle.dualWrap)}>
                <article className={formStyle.col}>
                  <Cleave disabled={isPosting || isPutting}
                          className={classNames(inputStyle.input, { [inputStyle.red]: expireMonth.errors && expireMonth.errors.length },
                            { [inputStyle.lightBlue]: !!expireMonth.value })}
                          value={expireMonth.value}
                          onChange={this.handleChange.bind(this)}
                          options={{ date: true, datePattern: ['mm'] }}
                          name="expireMonth"
                          placeholder={this.context.intl.formatMessage(messages.expireMonth.placeholder)}/>
                  {expireMonthError}
                </article>
                <article className={formStyle.col}>
                  <Cleave disabled={isPosting || isPutting}
                          className={classNames(inputStyle.input, { [inputStyle.red]: expireYear.errors && expireYear.errors.length },
                            { [inputStyle.lightBlue]: !!expireYear.value })}
                          value={expireYear.value}
                          onChange={this.handleChange.bind(this)}
                          options={{ date: true, datePattern: ['Y'] }}
                          name="expireYear"
                          placeholder={this.context.intl.formatMessage(messages.expireYear.placeholder)}/>
                </article>
              </section>
              {expireYearError}
            </article>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.CSV.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.CSV.placeholder.id}>{
                txt => <Cleave disabled={isPosting || isPutting}
                               className={classNames(inputStyle.input, { [inputStyle.red]: CSV.errors && CSV.errors.length },
                                 { [inputStyle.lightBlue]: !!CSV.value })}
                               value={CSV.value}
                               onChange={this.handleChange.bind(this)}
                               options={{ blocks: [4] }}
                               name="CSV"
                               placeholder={txt}
                               type="number"/>
              }</FormattedMessage>
              {CSVError}
            </article>
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap)}>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.firstName.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.firstName.placeholder.id}>{
                txt => <input disabled={isPosting || isPutting}
                              className={classNames(inputStyle.input, { [inputStyle.red]: firstName.errors && firstName.errors.length },
                                { [inputStyle.lightBlue]: !!firstName.value })}
                              value={firstName.value}
                              onChange={this.handleChange.bind(this)}
                              placeholder={txt}
                              name="firstName"/>
              }</FormattedMessage>
              {firstNameError}
            </article>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.lastName.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.lastName.placeholder.id}>{
                txt => <input disabled={isPosting || isPutting}
                              className={classNames(inputStyle.input, { [inputStyle.red]: lastName.errors && lastName.errors.length },
                                { [inputStyle.lightBlue]: !!lastName.value })}
                              value={lastName.value}
                              onChange={this.handleChange.bind(this)}
                              placeholder={txt}
                              name="lastName"/>
              }</FormattedMessage>
              {lastNameError}
            </article>
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap)}>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.country.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.country.placeholder.id}>{
                txt => <Select options={options}
                               className={classNames({
                                 'light-blue': country.value,
                                 'red': country.errors,
                               })}
                               value={country.value}
                               disabled={isPosting || isPutting}
                               searchable={true}
                               placeholder={txt}
                               handleChange={this.handleChangeCountry.bind(this)}/>
              }</FormattedMessage>
              {countryError}
            </article>
            <article className={formStyle.col}>
              <FormattedMessage id={messages.zipCode.label.id}>{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <FormattedMessage id={messages.zipCode.placeholder.id}>{
                txt => <Cleave options={{ numericOnly: true, blocks: [5, 4], delimiter: ' ' }}
                               disabled={isPosting || isPutting}
                               className={classNames(inputStyle.input, { [inputStyle.red]: zipCode.errors && zipCode.errors.length },
                                 { [inputStyle.lightBlue]: !!zipCode.value })}
                               value={zipCode.value}
                               onChange={this.handleChange.bind(this)}
                               placeholder={txt}
                               name="zipCode"/>
              }</FormattedMessage>
              {zipCodeError}
            </article>
          </section>
          <section className={classNames(commonStyle.buttonsRow, commonStyle.buttons)}>
            <FormattedMessage id={messages.button.cancel.id}>{
              txt => <button type="button"
                             disabled={isPosting || isPutting}
                             className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                             onClick={this.handleCancel.bind(this)}>
                {txt}</button>
            }</FormattedMessage>
            <FormattedMessage id={payment ? messages.button.edit.id : messages.button.add.id}>{
              txt => <button type="button"
                             disabled={isPosting || isPutting}
                             className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             onClick={this.handleSubmit.bind(this)}>
                {txt}</button>
            }</FormattedMessage>
          </section>
        </form>
      </Modal>
    );
  }
}

PaymentModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  postPayment: PropTypes.func.isRequired,
  editPayment: PropTypes.func.isRequired,
  payment: PropTypes.object,
  isPosting: PropTypes.bool,
  isPutting: PropTypes.bool,
  paymentModalVisible: PropTypes.bool.isRequired,
  cleanModalState: PropTypes.bool,
  localValues: PropTypes.object.isRequired,
};

PaymentModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/payments/components/PaymentModal/PaymentModal.jsx